import { useMemo } from 'react';
import { AlertCircle, CheckCircle2, RotateCcw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useToggleCommentResolutionMutation } from '@/redux/commet/comment.api.slice';

export interface AdminComment {
  _id: string;
  comment: string;
  user?: {
    username: string;
    firstname?: string;
    lastname?: string;
    email: string;
  };
  commentedAt: string;
  isResolved?: boolean;
  resolvedAt?: string | null;
}

interface AdminCommentsPanelProps {
  comments: AdminComment[];
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  onRefetch?: () => void;
}

interface CommentCardProps {
  comment: AdminComment;
  onToggle?: (commentId: string) => void;
  isLoading: boolean;
  showToggleButton: boolean;
}

function CommentCard({
  comment,
  onToggle,
  isLoading,
  showToggleButton,
}: CommentCardProps) {
  return (
    <div
      className={`p-4 rounded-lg border shadow-sm ${
        comment.isResolved ? 'bg-muted/50 border-muted' : 'bg-muted/30'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-xs font-semibold text-primary">
              {comment.user?.firstname?.[0] ||
                comment.user?.username?.[0] ||
                'A'}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium">
              {comment.user?.firstname && comment.user?.lastname
                ? `${comment.user.firstname} ${comment.user.lastname}`
                : comment.user?.username || 'Admin'}
            </p>
            <p className="text-xs text-muted-foreground">
              {new Date(comment.commentedAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {comment.isResolved && (
            <Badge variant="secondary" className="text-green-600 bg-green-50">
              Resolved
            </Badge>
          )}
          {showToggleButton && onToggle && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggle(comment._id)}
              disabled={isLoading}
              className="gap-1.5"
            >
              {comment.isResolved ? (
                <RotateCcw className="h-4 w-4" />
              ) : (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              )}
              {comment.isResolved ? 'Reopen' : 'Resolve'}
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm whitespace-pre-wrap">{comment.comment}</p>
    </div>
  );
}

export function AdminCommentsList({
  comments,
  token,
  onRefetch,
}: Omit<AdminCommentsPanelProps, 'isOpen' | 'onClose'>) {
  const [toggleResolution, { isLoading }] =
    useToggleCommentResolutionMutation();

  const handleToggle = async (commentId: string) => {
    if (!token) return;
    try {
      await toggleResolution({ commentId, token }).unwrap();
      // Refetch the post data to get updated comments
      if (onRefetch) {
        onRefetch();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle resolution:', error);
    }
  };

  // Separate comments into resolved and unresolved
  const { unresolvedComments, resolvedComments } = useMemo(() => {
    const unresolved = comments.filter((c) => !c.isResolved);
    const resolved = comments.filter((c) => c.isResolved);
    return { unresolvedComments: unresolved, resolvedComments: resolved };
  }, [comments]);

  const showToggleButton = !!token;

  return (
    <div className="space-y-4">
      {/* Unresolved Comments - Always Visible */}
      {unresolvedComments.length > 0 && (
        <div className="space-y-3">
          {unresolvedComments.map((comment) => (
            <CommentCard
              key={comment._id}
              comment={comment}
              onToggle={handleToggle}
              isLoading={isLoading}
              showToggleButton={showToggleButton}
            />
          ))}
        </div>
      )}

      {/* Resolved Comments - Accordion, Initially Closed */}
      {resolvedComments.length > 0 && (
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="resolved-comments" className="border-none">
            <AccordionTrigger className="hover:bg-muted/50 rounded-md px-2 py-2">
              <span className="text-sm font-medium">
                Resolved Comments ({resolvedComments.length})
              </span>
            </AccordionTrigger>
            <AccordionContent className="mt-3 space-y-3 pb-0">
              {resolvedComments.map((comment) => (
                <CommentCard
                  key={comment._id}
                  comment={comment}
                  onToggle={handleToggle}
                  isLoading={isLoading}
                  showToggleButton={showToggleButton}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}

function AdminCommentsPanel({
  comments,
  isOpen,
  onClose,
  token,
  onRefetch,
}: AdminCommentsPanelProps) {
  if (!comments || comments.length === 0) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-[600px] sm:w-[900px] max-w-[95vw]"
      >
        <SheetHeader className="border-b pb-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-primary" />
            <SheetTitle>Admin Feedback</SheetTitle>
          </div>
          <SheetDescription>
            The following feedback was provided by the Onefinity team
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 overflow-y-auto max-h-[calc(100vh-200px)]">
          <AdminCommentsList
            comments={comments}
            token={token}
            onRefetch={onRefetch}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default AdminCommentsPanel;
